import { c as _c } from "react/compiler-runtime";
import type { AsanaInstallations } from '@/generated/prisma/browser';
import type { AsanaWorkspace } from '@/lib/asana/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { brokenIntegrationsQueryKey } from '@/data/integrations/use-broken-integrations';
import { refetchSelectedSharedPmConnection } from '@/data/nango/shared-pm-realtime';
import { useNangoIntegration } from '@/data/nango/use-nango-integration';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { getIntegrationConnectionState } from '@/lib/integrations/auth-status';
import { REALTIME_KEYS } from '@/lib/realtime/types';
export const asanaQueryKey = (installationId?: number) => ['asana-connection', installationId !== undefined ? String(installationId) : 'no-installation'];
export function useAsanaIntegration(installationId) {
  const $ = _c(52);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== installationId) {
    t0 = asanaQueryKey(installationId);
    $[0] = installationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0;
  let t1;
  if ($[2] !== installationId) {
    t1 = async t2 => {
      const {
        useContextForReview,
        useContextForPrDescription,
        automaticallyAssociateIssues,
        showLinkedIssueSummary
      } = t2;
      const params = new URLSearchParams({
        useContextForReview: String(useContextForReview),
        useContextForPrDescription: String(useContextForPrDescription),
        automaticallyAssociateIssues: String(automaticallyAssociateIssues),
        showLinkedIssueSummary: String(showLinkedIssueSummary)
      });
      if (installationId !== undefined) {
        params.append("installationId", String(installationId));
      }
      const res = await fetch(`/api/asana/settings?${params.toString()}`, {
        method: "PATCH"
      });
      if (!res.ok) {
        throw new Error("Failed to update Asana settings");
      }
      return res.json();
    };
    $[2] = installationId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  let t3;
  let t4;
  if ($[4] !== queryClient || $[5] !== queryKey) {
    t2 = async variables => {
      await queryClient.cancelQueries({
        queryKey
      });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          useContextForReview: variables.useContextForReview,
          useContextForPrDescription: variables.useContextForPrDescription,
          automaticallyAssociateIssues: variables.automaticallyAssociateIssues,
          showLinkedIssueSummary: variables.showLinkedIssueSummary
        };
      });
      return {
        previous
      };
    };
    t3 = (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error("Failed to update Asana settings");
    };
    t4 = data => {
      queryClient.setQueryData(queryKey, old_0 => {
        if (!old_0) {
          return old_0;
        }
        return {
          ...old_0,
          useContextForReview: data.settings.useContextForReview,
          useContextForPrDescription: data.settings.useContextForPrDescription,
          showLinkedIssueSummary: data.settings.showLinkedIssueSummary,
          ...(typeof data.settings.automaticallyAssociateIssues === "boolean" && {
            automaticallyAssociateIssues: data.settings.automaticallyAssociateIssues
          })
        };
      });
      toast.success("Asana settings updated");
    };
    $[4] = queryClient;
    $[5] = queryKey;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      mutationFn: t1,
      onMutate: t2,
      onError: t3,
      onSuccess: t4
    };
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const settingsMutation = useMutation(t5);
  let t6;
  if ($[14] !== installationId) {
    t6 = async workspace => {
      const url = new URL("/api/asana/workspaces/set", window.location.origin);
      if (installationId !== undefined) {
        url.searchParams.append("installationId", String(installationId));
      }
      const res_0 = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          workspaceGid: workspace.gid,
          workspaceName: workspace.name
        })
      });
      if (!res_0.ok) {
        throw new Error("Failed to set Asana workspace");
      }
      return res_0.json();
    };
    $[14] = installationId;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  let t8;
  let t9;
  if ($[16] !== queryClient || $[17] !== queryKey) {
    t7 = async workspace_0 => {
      await queryClient.cancelQueries({
        queryKey
      });
      const previous_0 = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old_1 => {
        if (!old_1) {
          return old_1;
        }
        return {
          ...old_1,
          workspaceGid: workspace_0.gid,
          workspaceName: workspace_0.name
        };
      });
      return {
        previous: previous_0
      };
    };
    t8 = (_err_0, _workspace, context_0) => {
      if (context_0?.previous) {
        queryClient.setQueryData(queryKey, context_0.previous);
      }
      toast.error("Failed to set Asana workspace");
    };
    t9 = data_0 => {
      queryClient.setQueryData(queryKey, old_2 => {
        if (!old_2) {
          return old_2;
        }
        return {
          ...old_2,
          workspaceGid: data_0.settings.workspaceGid,
          workspaceName: data_0.settings.workspaceName
        };
      });
      toast.success("Asana workspace updated");
    };
    $[16] = queryClient;
    $[17] = queryKey;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t7 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t6 || $[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t10 = {
      mutationFn: t6,
      onMutate: t7,
      onError: t8,
      onSuccess: t9
    };
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const workspaceMutation = useMutation(t10);
  const t11 = installationId ? `/api/asana/connections/get?installationId=${installationId}` : "/api/asana/connections/get";
  const t12 = installationId ? `/api/asana/connections/delete?installationId=${installationId}` : "/api/asana/connections/delete";
  const t13 = installationId !== undefined;
  let t14;
  if ($[26] !== installationId || $[27] !== queryClient) {
    t14 = {
      onConnectAttempt: _temp,
      onConnectSuccess: connection => {
        queryClient.invalidateQueries({
          queryKey: brokenIntegrationsQueryKey(installationId)
        });
        toast.dismiss("asana-refresh-failure");
        toast.success("Asana account connected");
        trackClientEvent("asana_connection_successful", undefined, {
          workspace_gid: connection?.workspaceGid,
          workspace_name: connection?.workspaceName
        });
      },
      onConnectError: _temp2,
      onDisconnectSuccess: _temp3,
      onDisconnectError: _temp4,
      onPollingTimeout: _temp5
    };
    $[26] = installationId;
    $[27] = queryClient;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== installationId || $[30] !== queryKey || $[31] !== t11 || $[32] !== t12 || $[33] !== t13 || $[34] !== t14) {
    t15 = {
      provider: "asana",
      getConnectionUrl: t11,
      disconnectUrl: t12,
      realtimeEventKey: REALTIME_KEYS.ASANA_CONNECTION_UPDATE,
      queryKey,
      installationId,
      enabled: t13,
      getConnectionState: getIntegrationConnectionState,
      callbacks: t14,
      onRealtimeEventAction: _temp6
    };
    $[29] = installationId;
    $[30] = queryKey;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const nangoIntegration = useNangoIntegration(t15);
  let t16;
  if ($[36] !== queryClient || $[37] !== queryKey) {
    t16 = function getCurrentConnection() {
      return queryClient.getQueryData(queryKey) as AsanaInstallations | undefined;
    };
    $[36] = queryClient;
    $[37] = queryKey;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const getCurrentConnection = t16;
  let t17;
  if ($[39] !== getCurrentConnection || $[40] !== settingsMutation) {
    t17 = t18 => {
      const partial = t18 === undefined ? {} : t18;
      const current = getCurrentConnection();
      const merged = {
        useContextForReview: typeof partial.useContextForReview === "boolean" ? partial.useContextForReview : current?.useContextForReview ?? true,
        useContextForPrDescription: typeof partial.useContextForPrDescription === "boolean" ? partial.useContextForPrDescription : current?.useContextForPrDescription ?? true,
        automaticallyAssociateIssues: typeof partial.automaticallyAssociateIssues === "boolean" ? partial.automaticallyAssociateIssues : current?.automaticallyAssociateIssues ?? false,
        showLinkedIssueSummary: typeof partial.showLinkedIssueSummary === "boolean" ? partial.showLinkedIssueSummary : current?.showLinkedIssueSummary ?? true
      };
      settingsMutation.mutate(merged);
    };
    $[39] = getCurrentConnection;
    $[40] = settingsMutation;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== workspaceMutation) {
    t18 = workspace_1 => workspaceMutation.mutateAsync(workspace_1);
    $[42] = workspaceMutation;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  const t19 = settingsMutation.error?.message || "";
  const t20 = workspaceMutation.error?.message || "";
  let t21;
  if ($[44] !== nangoIntegration || $[45] !== settingsMutation.isPending || $[46] !== t17 || $[47] !== t18 || $[48] !== t19 || $[49] !== t20 || $[50] !== workspaceMutation.isPending) {
    t21 = {
      ...nangoIntegration,
      updateAsanaSettings: t17,
      setAsanaWorkspace: t18,
      isUpdatingSettings: settingsMutation.isPending,
      isSettingWorkspace: workspaceMutation.isPending,
      settingsError: t19,
      workspaceError: t20
    };
    $[44] = nangoIntegration;
    $[45] = settingsMutation.isPending;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
    $[50] = workspaceMutation.isPending;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  return t21;
}
function _temp6(payload, t0) {
  const {
    queryClient: queryClient_0,
    queryKey: queryKey_0,
    setIsPolling
  } = t0;
  if (payload?.refreshFailure) {
    refetchSelectedSharedPmConnection({
      queryClient: queryClient_0,
      queryKey: queryKey_0,
      setIsPolling
    });
    toast.error("Your Asana connection expired. Please reconnect.", {
      id: "asana-refresh-failure"
    });
    trackClientEvent("asana_connection_failed", undefined, {
      reason: "refresh_failure",
      connection_id: payload.refreshFailure.connectionId,
      error_type: payload.refreshFailure.errorType
    });
    return;
  }
  refetchSelectedSharedPmConnection({
    queryClient: queryClient_0,
    queryKey: queryKey_0,
    setIsPolling
  });
}
function _temp5(attempts) {
  trackClientEvent("asana_connection_failed", undefined, {
    reason: "polling_timeout",
    attempts
  });
  toast.error("Failed to confirm Asana connection. Please try again.");
}
function _temp4(error) {
  toast.error(error.message || "Failed to disconnect Asana");
}
function _temp3() {
  toast.success("Asana account disconnected");
}
function _temp2(_error, reason) {
  if (reason === "popup_blocked") {
    toast.error("Browser blocked the Asana authentication popup. Please allow popups for this site and try again.");
    trackClientEvent("asana_connection_failed", undefined, {
      reason: "popup_blocked"
    });
  } else {
    toast.error("Failed to connect Asana");
    trackClientEvent("asana_connection_failed", undefined, {
      reason: reason || "auth_error"
    });
  }
}
function _temp() {
  trackClientEvent("asana_connection_attempt", undefined);
}